import { customReportsApi } from '~/custom_reports/api'
import { Link, useErrorModal, useInfoModal, useSuccessToast } from '~/design_system'
import { useMsg } from '~/common/localizationUtils'
import { REPORT_FILES_PATH } from '~/custom_reports/constants'
import { taskProps } from './ReportExport'

const DEFAULT_RETRY_TIME = 3000

type pageDataProps = {
  pageData:
    | {
        pageSize: number
        index: number
      }
    | undefined
  filters: {
    id: string
    values: {
      [key: string]: string
    }
  }[]
  sortColumn: {
    id: string
    orderBy: string
  }
}
export const buildQueryParams = ({ pageData = undefined, filters = [], sortColumn }: pageDataProps) => {
  const pageParams = pageData
    ? {
        limit: pageData.pageSize,
        offset: pageData.index * pageData.pageSize
      }
    : {}

  const filterParams = filters.reduce<{ [key: string]: string }>((acc, filter) => {
    acc[`filter__${filter.id}__${filter.values.filter_operator}`] = filter.values[filter.id]
    return acc
  }, {})

  const sortParams = sortColumn.id
    ? {
        [`order_by__${sortColumn.orderBy}`]: sortColumn.id
      }
    : {}

  return { ...pageParams, ...filterParams, ...sortParams }
}

export const getSortColumn = (queryParams: { order_by__asc?: string; order_by__desc?: string } = {}) => {
  if ('order_by__asc' in queryParams) {
    return {
      id: queryParams.order_by__asc,
      orderBy: 'asc'
    }
  }
  if ('order_by__desc' in queryParams) {
    return {
      id: queryParams.order_by__desc,
      orderBy: 'desc'
    }
  }
  return {}
}

export const getFilters = (queryParams: Record<string, string> = {}) =>
  Object.keys(queryParams)
    .filter(queryParam => queryParam.startsWith('filter'))
    .map(queryParam => {
      const [, id, filter_operator = 'exact'] = queryParam.split('__')
      return {
        id,
        values: {
          filter_operator,
          [id]: queryParams[queryParam]
        }
      }
    })

type getPageDataProps = {
  limit: string
  offset: string
}
export const getPageData = ({ limit = '10', offset = '0' }: getPageDataProps) => ({
  index: parseInt(offset, 10) / parseInt(limit, 10),
  pageSize: parseInt(limit, 10)
})

type headerProps = {
  Header: string
}
export const alphabeticalSortHeader = (a: headerProps, b: headerProps) => {
  const transform = (str: string) => str.split(' ').join('').toLowerCase()
  return transform(a.Header) < transform(b.Header) ? -1 : 1
}

type waitAndDownloadOptionsProps = {
  onTaskSuccess?: (task: taskProps) => void
  onTaskFail?: (task: taskProps) => void
  onRetryLimitReached?: (task: taskProps) => void
  title?: string
  showAlertModal?: boolean
  maxTries?: number
}

type artifactProps = {
  path: string
}

type reportFileProps = {
  id: number
  saved_report_id: number
  path: string
  created_at: string
  created_by: number
}

export enum ExportStatusEnum {
  Success = 'success',
  PartialSuccess = 'partial_success',
  Processing = 'processing',
  Failure = 'failure'
}

export function useWaitAndDownload() {
  const successToast = useSuccessToast()
  const { showModal: showErrorModal } = useErrorModal()
  const { showModal: showInfoModal } = useInfoModal()

  return async function waitAndDownload(taskId: string, options = {}) {
    const {
      onTaskSuccess,
      onTaskFail,
      onRetryLimitReached,
      maxTries = 30,
      title = 'Downloading',
      showAlertModal = true
    }: waitAndDownloadOptionsProps = options
    if (maxTries <= 0) {
      if (typeof onRetryLimitReached === 'function') {
        onRetryLimitReached({})
      }
      if (showAlertModal) {
        await showInfoModal({
          title: 'Export is taking longer than expected',
          message: (
            <Link variant="link" href={REPORT_FILES_PATH} target="_blank">
              Click here to check the job status
            </Link>
          ),
          size: 'lg'
        })
      }
      return
    }
    setTimeout(async () => {
      const task = await customReportsApi.getExportTaskStatus(taskId)
      if (task.status === 'completed') {
        if (typeof onTaskSuccess === 'function') {
          onTaskSuccess(task)
        }
        if (showAlertModal) {
          successToast(title)
        }
        customReportsApi
          .getArtifact(task.result.export_artifact_id)
          .then((artifact: artifactProps) => window.location.assign(artifact.path))
      } else if (task.status === 'failed') {
        if (typeof onTaskFail === 'function') {
          onTaskFail(task)
        }
        if (showAlertModal) {
          showErrorModal({
            title: 'Error Exporting Report',
            message: (
              <Link variant="link" href={REPORT_FILES_PATH} target="_blank">
                Click here to check the job status
              </Link>
            )
          })
        }
      } else {
        waitAndDownload(taskId, { ...options, maxTries: maxTries - 1 })
      }
    }, DEFAULT_RETRY_TIME)
  }
}

export function useWaitAndDownloadReportFile() {
  const msg = useMsg()
  const successToast = useSuccessToast()
  const { showModal: showErrorModal } = useErrorModal()
  const { showModal: showInfoModal } = useInfoModal()
  return async function waitAndDownload(trackingId: string, options = {}) {
    const {
      onTaskSuccess,
      onTaskFail,
      onRetryLimitReached,
      maxTries = 30,
      title = 'Downloading',
      showAlertModal = true
    }: waitAndDownloadOptionsProps = options
    if (maxTries <= 0) {
      if (typeof onRetryLimitReached === 'function') {
        onRetryLimitReached({})
      }
      if (showAlertModal) {
        await showInfoModal({
          title: msg('custom_report.export.long_running_report'),
          message: (
            <Link variant="link" href={REPORT_FILES_PATH} target="_blank">
              {msg('custom_report.export.check_status')}
            </Link>
          ),
          size: 'lg'
        })
      }
      return
    }
    setTimeout(async () => {
      const exportStatus = await customReportsApi.getReportFileExportStatus(trackingId).catch(() => {
        if (typeof onTaskFail === 'function') {
          onTaskFail({
            result: {
              message: msg('custom_report.export.failure')
            }
          })
        }
      })

      if (exportStatus.status === ExportStatusEnum.Success || exportStatus.status === ExportStatusEnum.PartialSuccess) {
        if (typeof onTaskSuccess === 'function') {
          onTaskSuccess({
            result: {
              message:
                exportStatus.status === ExportStatusEnum.PartialSuccess
                  ? msg('custom_report.export.partial_success')
                  : msg('custom_report.export.success')
            }
          })
        }
        if (showAlertModal) {
          successToast(title)
        }
        customReportsApi
          .getReportFile(trackingId)
          .then((reportFile: reportFileProps) => window.location.assign(reportFile.path))
      } else if (exportStatus.status === ExportStatusEnum.Failure) {
        if (typeof onTaskFail === 'function') {
          onTaskFail({
            result: {
              message: msg('custom_report.export.failure')
            }
          })
        }
        if (showAlertModal) {
          showErrorModal({
            title: msg('custom_report.export.error'),
            message: (
              <Link variant="link" href={REPORT_FILES_PATH} target="_blank">
                {msg('custom_report.export.check_status')}
              </Link>
            )
          })
        }
      } else {
        await waitAndDownload(trackingId, { ...options, maxTries: maxTries - 1 })
      }
    }, DEFAULT_RETRY_TIME)
  }
}
